import { useState } from "react";

/**
 * Confirm-void modal — Phase 11.
 *
 * Voids are destructive and within a 30-minute window after a scan,
 * per Phase 10's `TRANSACTION_TOO_OLD_TO_VOID` rule. We surface that
 * window inline so the cashier doesn't tap "Void" then bounce off a
 * server 400 with no context.
 *
 * The reason field is a free text input min 5 chars (matches Phase 10
 * Zod). On submit the parent runs the void mutation; this component
 * only owns the form state + the busy spinner.
 */
export function VoidConfirmModal(props: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  customerName: string;
  amount: string | null;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!props.open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (reason.trim().length < 5) {
      setErr("Reason must be at least 5 characters.");
      return;
    }
    setBusy(true);
    try {
      await props.onConfirm(reason.trim());
      setReason("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Void failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-card bg-white p-5 shadow-lg">
        <h2 className="text-lg font-bold text-navy">Void this transaction?</h2>
        <p className="mt-1 text-sm text-ink-dark">
          {props.customerName}
          {props.amount ? ` · ₱${props.amount}` : ""}
        </p>
        <p className="mt-2 text-xs text-ink-dark">
          Voiding fully reverses the transaction (loyalty + financial). Only
          allowed within 30 minutes of the scan.
        </p>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
              Reason
            </span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
              required
              minLength={5}
              maxLength={200}
              placeholder="e.g. Wrong amount entered"
              className="mt-1 input"
            />
          </label>
          {err && (
            <div className="rounded-card border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {err}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={props.onClose}
              disabled={busy}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || reason.trim().length < 5}
              className="btn-primary flex-1"
            >
              {busy ? "Voiding…" : "Void"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
